const RAW_KEYCLOAK_ADMIN_URL = import.meta.env.VITE_KEYCLOAK_ADMIN_URL ?? ''

/**
 * Selects the Keycloak admin-console URL from runtime config first, falling
 * back to Vite build-time config for local static-image builds.
 *
 * Absent and empty-string are deliberately NOT equivalent:
 * - `undefined` (key omitted from `public/config.js`) means "no runtime
 *   opinion" and defers to the build-time value — this is how compose and
 *   `npm run dev` surface their link.
 * - `''` (key present but blank, e.g. the Helm `web.keycloakAdminUrl` default)
 *   means this deployment explicitly disables the link, and wins over a URL
 *   baked into the image at build time.
 */
export function selectKeycloakAdminUrl(
  runtimeUrl: string | undefined,
  buildTimeUrl = RAW_KEYCLOAK_ADMIN_URL,
): string {
  return runtimeUrl ?? buildTimeUrl
}

function readConfiguredKeycloakAdminUrl(): string {
  if (typeof window === 'undefined') {
    return RAW_KEYCLOAK_ADMIN_URL
  }

  return selectKeycloakAdminUrl(window.__ENV__?.KEYCLOAK_ADMIN_URL, RAW_KEYCLOAK_ADMIN_URL)
}

/**
 * Resolves the configured Keycloak admin-console URL for the shell admin menu.
 * Empty or whitespace-only values disable the link so deployments without an
 * exposed Keycloak console do not show a dead navigation item.
 */
export function resolveKeycloakAdminUrl(
  configuredUrl = readConfiguredKeycloakAdminUrl(),
): string | null {
  const trimmed = configuredUrl.trim()
  return trimmed.length > 0 ? trimmed : null
}
